import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../services/api";
import { ireneLiveApi } from "../../services/ireneLiveApi";
import { Save } from "lucide-react";
import {
  IRENE_MODEL_OPTIONS, IRENE_SOL_EFFORT_OPTIONS,
  loadIreneModelSettings, saveIreneModelSettings,
} from "../../services/ireneModelSettings";
import RefreshButton from "../RefreshButton/RefreshButton";
import IreneTopicMemory from "./IreneTopicMemory";
import useHashTab from "../../hooks/useHashTab";
import "./IreneSessions.css";

const IRENE_TABS = [
  { id: "sessions", label: "Sessions" },
  { id: "ai-memory", label: "AI Memory" },
  { id: "ai-memory-prompt", label: "AI Memory Prompt" },
  { id: "core", label: "Core" },
  { id: "cloud-prompt", label: "Cloud Prompt" },
  { id: "instructions", label: "Instructions" },
  { id: "settings", label: "Settings" },
];

const IRENE_TAB_IDS = IRENE_TABS.map(({ id }) => id);

function formatNumber(n) {
  if (n === null || n === undefined || n === "") return "0";
  const v = typeof n === "number" ? n : parseInt(n, 10);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

function copyJson(obj) {
  try {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
  } catch {
    /* no-op */
  }
}

function CollapsibleBlock({ title, defaultOpen = false, stat, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="prompt-section" style={{ marginBottom: "0.75rem" }}>
      <div
        className="section-header"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((v) => !v)}
      >
        <h4>{open ? "▾" : "▸"} {title}</h4>
        {stat && <span className="section-stat">{stat}</span>}
      </div>
      {open && children}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    completed: { color: "#0a0", label: "✅ completed" },
    failed: { color: "#c33", label: "❌ failed" },
    needs_clarification: { color: "#c80", label: "❓ needs clarification" },
    running: { color: "#06c", label: "⏳ running" },
  };
  const m = map[status] || { color: "#888", label: status || "unknown" };
  return (
    <span className="token-badge" style={{ color: m.color }}>{m.label}</span>
  );
}

function LiveTaskCard({ task }) {
  return (
    <section className="irene-live" aria-live="polite">
      <div className="irene-live-heading">
        <div>
          <span className="irene-live-dot" aria-hidden="true" />
          <strong>Irene is working</strong>
        </div>
        <span>{Math.round(task.elapsedSeconds || 0)}s elapsed</span>
      </div>
      <div className="irene-live-track"><span /></div>
      <p className="irene-live-task">{task.task || "Task details are loading…"}</p>
      <div className="irene-live-status">
        <strong>{task.activity || "Starting task"}</strong>
        <div>
          <span>{task.iterationCount || 0} iterations</span>
          <span>{task.commandCount || 0} commands</span>
          {task.updatedAt && <span>updated {new Date(task.updatedAt).toLocaleTimeString()}</span>}
        </div>
      </div>
    </section>
  );
}

function IreneModelSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [persisted, setPersisted] = useState(null);
  const [model, setModel] = useState("gpt-6-luna");
  const [effort, setEffort] = useState("low");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const settings = await loadIreneModelSettings(apiClient);
      setModel(settings.model);
      setEffort(settings.model === "gpt-6.1-sol" ? settings.reasoning_effort : "low");
      setPersisted(settings);
    } catch (err) {
      setError(err.message || "Could not load Irene model settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reasoningEffort = model === "gpt-6.1-sol" ? effort : "none";
  const unchanged = persisted?.model === model && persisted?.reasoning_effort === reasoningEffort;
  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const settings = await saveIreneModelSettings(apiClient, { model, reasoning_effort: reasoningEffort });
      setPersisted(settings);
      setSaved(true);
    } catch (err) {
      setError(err.message || "Could not save Irene model settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="core-prompt-section">
      <div className="section-header">
        <h3>Cloud Model</h3>
        <RefreshButton onClick={load} title="Reload Irene model settings" disabled={loading || saving} />
      </div>
      {loading ? <div className="placeholder">Loading model settings...</div> : (
        <>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
            <label className="variable-item" style={{ flex: "1 1 220px", minWidth: 0 }}>
              <div className="variable-label">Model</div>
              <select
                className="instruction-category-input"
                aria-label="Irene model"
                style={{ width: "100%" }}
                value={model}
                disabled={saving || !persisted}
                onChange={(event) => { setModel(event.target.value); setSaved(false); }}
              >
                {IRENE_MODEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            {model === "gpt-6.1-sol" && (
              <label className="variable-item" style={{ flex: "1 1 180px", minWidth: 0 }}>
                <div className="variable-label">Reasoning Effort</div>
                <select
                  className="instruction-category-input"
                  aria-label="Sol reasoning effort"
                  style={{ width: "100%" }}
                  value={effort}
                  disabled={saving || !persisted}
                  onChange={(event) => { setEffort(event.target.value); setSaved(false); }}
                >
                  {IRENE_SOL_EFFORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            )}
          </div>
          <div className="prompt-actions">
            <button className="btn-primary" type="button" onClick={save} disabled={saving || !persisted || unchanged}>
              <Save size={16} aria-hidden="true" /> {saving ? "Saving..." : "Save model"}
            </button>
            {saved && <span role="status" className="save-confirm">Saved</span>}
          </div>
        </>
      )}
      {error && <p role="alert" className="irene-live-error">{error}</p>}
    </div>
  );
}

function CallbackReentrySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [maxDepth, setMaxDepth] = useState("2");
  const [maxIterations, setMaxIterations] = useState("4");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/settings");
      const settings = data.settings || {};
      setEnabled((settings.callback_reentry_irene_enabled?.value || "true") !== "false");
      setMaxDepth(settings.callback_reentry_max_depth?.value || "2");
      setMaxIterations(settings.callback_reentry_max_iterations?.value || "4");
    } catch (err) {
      console.error("Failed to fetch callback re-entry settings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/settings", {
        settings: {
          callback_reentry_irene_enabled: String(enabled),
          callback_reentry_max_depth: String(maxDepth),
          callback_reentry_max_iterations: String(maxIterations),
        },
      });
      await load();
    } catch (err) {
      console.error("Failed to save callback re-entry settings:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        Central callback re-entry controls for delegated Irene results. These limit how many times Anna can re-enter full analysis and re-delegate again after an Irene callback.
      </p>
      {loading ? (
        <div className="placeholder">Loading callback re-entry settings…</div>
      ) : (
        <div className="prompt-section">
          <div className="variable-item" style={{ marginBottom: "0.75rem" }}>
            <label className="variable-label" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enable Irene callback re-entry
            </label>
          </div>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <label className="variable-item" style={{ flex: "1 1 180px" }}>
              <div className="variable-label">Max callback depth</div>
              <input className="instruction-category-input" type="number" min="0" max="10" value={maxDepth} onChange={(e) => setMaxDepth(e.target.value)} />
            </label>
            <label className="variable-item" style={{ flex: "1 1 180px" }}>
              <div className="variable-label">Max Anna passes</div>
              <input className="instruction-category-input" type="number" min="1" max="8" value={maxIterations} onChange={(e) => setMaxIterations(e.target.value)} />
            </label>
          </div>
          <button className="btn-primary" type="button" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save callback re-entry settings"}
          </button>
        </div>
      )}
    </div>
  );
}

/** Render one Gemma↔tools iteration from the agent's `messages` log. */
function IterationCard({ message, idx }) {
  const role = message.role;
  if (role === "system" || role === "user") {
    return (
      <div className="prompt-log-detail" style={{
        border: "1px dashed #aab", borderRadius: 6, padding: "0.5rem", marginBottom: "0.5rem",
      }}>
        <div className="section-stat">#{idx} · {role}</div>
        <pre className="prompt-content">{message.content || "(empty)"}</pre>
      </div>
    );
  }

  if (role === "assistant") {
    const toolCalls = message.tool_calls || [];
    return (
      <div className="prompt-log-detail" style={{
        border: "1px solid #ccd", borderRadius: 6, padding: "0.75rem", marginBottom: "0.75rem",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem",
        }}>
          <strong>#{idx} · Gemma</strong>
          <span className="section-stat">{toolCalls.length} tool call(s)</span>
        </div>

        {message.thinking && (
          <CollapsibleBlock title="Thinking" stat={`${formatNumber(message.thinking.length)} chars`}>
            <pre className="prompt-content" style={{ opacity: 0.85 }}>{message.thinking}</pre>
          </CollapsibleBlock>
        )}

        {message.content && (
          <CollapsibleBlock
            title="Response"
            defaultOpen={!toolCalls.length}
            stat={`${formatNumber(message.content.length)} chars`}
          >
            <pre className="prompt-content">{message.content}</pre>
          </CollapsibleBlock>
        )}

        {toolCalls.length > 0 && (
          <CollapsibleBlock title={`Tool calls (${toolCalls.length})`} defaultOpen={true}>
            {toolCalls.map((tc, i) => {
              const fn = tc.function || {};
              const args = typeof fn.arguments === "string"
                ? (() => { try { return JSON.parse(fn.arguments); } catch { return fn.arguments; } })()
                : fn.arguments;
              return (
                <div key={i} className="variable-item" style={{ marginBottom: "0.5rem" }}>
                  <div className="variable-name">
                    <span className="variable-label">▶ {fn.name || "(unknown)"}</span>
                  </div>
                  <pre className="variable-value">{
                    typeof args === "string" ? args : JSON.stringify(args, null, 2)
                  }</pre>
                </div>
              );
            })}
          </CollapsibleBlock>
        )}
      </div>
    );
  }

  if (role === "tool") {
    let parsed = message.content;
    let exitCode = null;
    let stdout = null;
    let stderr = null;
    try {
      const obj = typeof message.content === "string" ? JSON.parse(message.content) : message.content;
      if (obj && typeof obj === "object") {
        exitCode = obj.exit_code;
        stdout = obj.stdout;
        stderr = obj.stderr;
        parsed = obj;
      }
    } catch {
      /* leave as raw string */
    }
    const ok = exitCode === 0;
    return (
      <div className="prompt-log-detail" style={{
        border: "1px solid #cdc", borderRadius: 6, padding: "0.75rem", marginBottom: "0.75rem",
        background: ok ? "rgba(0,160,0,0.04)" : "rgba(200,50,50,0.04)",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem",
        }}>
          <strong>#{idx} · {ok ? "✅" : "❌"} {message.name || "tool"}</strong>
          {exitCode !== null && (
            <span className="section-stat">exit {exitCode}</span>
          )}
        </div>
        {stdout && (
          <CollapsibleBlock title="stdout" defaultOpen={true} stat={`${formatNumber(stdout.length)} chars`}>
            <pre className="prompt-content">{stdout}</pre>
          </CollapsibleBlock>
        )}
        {stderr && (
          <CollapsibleBlock title="stderr" defaultOpen={!ok} stat={`${formatNumber(stderr.length)} chars`}>
            <pre className="prompt-content" style={{ color: "#c33" }}>{stderr}</pre>
          </CollapsibleBlock>
        )}
        {!stdout && !stderr && (
          <pre className="prompt-content">{
            typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2)
          }</pre>
        )}
      </div>
    );
  }

  return null;
}

// ---- Prompt editors ----

function IreneCore({ cloud = false }) {
  const endpoint = cloud ? "/irene-prompt?cloud=true" : "/irene-prompt";
  const promptName = cloud ? "Cloud Irene" : "Irene";
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [activeVersion, setActiveVersion] = useState(0);
  const [versions, setVersions] = useState([]);

  const estimateTokens = (text) => Math.ceil((text || "").length / 4);

  const fetchPrompt = useCallback(async () => {
    try {
      const data = await apiClient.get(endpoint);
      setPrompt(data.prompt || "");
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || 0);
      setVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch irene prompt:", err);
    }
  }, [endpoint]);

  useEffect(() => { fetchPrompt(); }, [fetchPrompt]);

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const data = await apiClient.put(endpoint, { prompt });
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || data.version || 0);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      const refreshed = await apiClient.get(endpoint);
      setVersions(refreshed.versions || []);
    } catch (err) {
      console.error("Failed to save irene prompt:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateVersion = async (version) => {
    if (!window.confirm(`Restore ${promptName} prompt version ${version}?`)) return;
    setLoading(true);
    try {
      await apiClient.put(endpoint, { activateVersion: version });
      await fetchPrompt();
    } catch (err) {
      console.error("Failed to activate irene version:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        {cloud ? "Cloud Irene's system prompt — used for tasks dispatched to the cloud worker." : "Local Irene's system prompt — used for tasks dispatched to irenewks."}
        Changes take effect on the next task dispatch.
        {activeVersion > 0 && <span className="prompt-version-badge">v{activeVersion}</span>}
      </p>
      <div className="prompt-token-bar">
        <span>{prompt.length.toLocaleString()} chars</span>
        <span className="prompt-token-sep">·</span>
        <span>~{estimateTokens(prompt).toLocaleString()} tokens</span>
      </div>
      <textarea
        className="prompt-editor"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder={`Enter ${promptName}'s system prompt...`}
        spellCheck={false}
      />
      <div className="prompt-actions">
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save as New Version"}
        </button>
        {saved && <span className="save-confirm">✓ Saved as v{activeVersion}</span>}
        {updatedAt && (
          <span className="prompt-updated">
            Last updated: {new Date(updatedAt).toLocaleString()}
          </span>
        )}
      </div>

      {versions.length > 0 && (
        <div className="prompt-versions">
          <h3>Version History</h3>
          <div className="versions-list">
            {versions.map((v) => (
              <div
                key={v.version}
                className={`version-item ${v.version === activeVersion ? "active" : ""}`}
              >
                <div className="version-info">
                  <span className="version-number">v{v.version}</span>
                  {v.version === activeVersion && <span className="version-active-tag">Active</span>}
                  <span className="version-date">
                    {v.createdAt ? new Date(v.createdAt).toLocaleString() : "—"}
                  </span>
                  <span className="version-chars">{v.charCount?.toLocaleString()} chars · ~{Math.ceil((v.charCount || 0) / 4).toLocaleString()} tokens</span>
                </div>
                <div className="version-preview">{v.preview}</div>
                {v.version !== activeVersion && (
                  <button
                    className="btn-restore"
                    onClick={() => handleActivateVersion(v.version)}
                    disabled={loading}
                  >
                    Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function IreneAIMemoryPrompt() {
  const [kind, setKind] = useState("extraction");
  const tabRefs = useRef([]);
  const promptKinds = ["extraction", "maintenance"];
  const handleTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % promptKinds.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + promptKinds.length) % promptKinds.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = promptKinds.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    setKind(promptKinds[nextIndex]);
    tabRefs.current[nextIndex]?.focus();
  };
  return (
    <>
      <div className="tab-bar" role="tablist" aria-label="AI Memory prompts">
        {promptKinds.map((promptKind, index) => (
          <button
            key={promptKind}
            type="button"
            ref={(node) => { tabRefs.current[index] = node; }}
            tabIndex={kind === promptKind ? 0 : -1}
            id={`memory-prompt-tab-${promptKind}`}
            className={`tab ${kind === promptKind ? "active" : ""}`}
            role="tab"
            aria-selected={kind === promptKind}
            aria-controls={`memory-prompt-panel-${promptKind}`}
            onClick={() => setKind(promptKind)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {promptKind === "extraction" ? "Extraction" : "Maintenance"}
          </button>
        ))}
      </div>
      {promptKinds.map((promptKind) => (
        <section
          key={promptKind}
          id={`memory-prompt-panel-${promptKind}`}
          role="tabpanel"
          aria-labelledby={`memory-prompt-tab-${promptKind}`}
          hidden={kind !== promptKind}
        >
          <IreneAIMemoryPromptEditor kind={promptKind} />
        </section>
      ))}
    </>
  );
}

function IreneAIMemoryPromptEditor({ kind }) {
  const maintenance = kind === "maintenance";
  const label = maintenance ? "Maintenance" : "Extraction";
  const endpoint = maintenance ? "/irene-topic-memory/maintenance-prompt" : "/irene-topic-memory/prompt";
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [source, setSource] = useState("");
  const [activeVersion, setActiveVersion] = useState(0);
  const [versions, setVersions] = useState([]);

  const fetchPrompt = useCallback(async () => {
    setFetching(true);
    setError("");
    try {
      const data = await apiClient.get(endpoint);
      setPrompt(data.prompt || "");
      setUpdatedAt(data.updatedAt || "");
      setSource(data.source || "");
      setActiveVersion(data.activeVersion || 0);
      setVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch Luna topic-memory prompt:", err);
      setError(err.data?.error || err.message || "Could not load the prompt.");
    } finally {
      setFetching(false);
    }
  }, [endpoint]);

  useEffect(() => { fetchPrompt(); }, [fetchPrompt]);

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    setError("");
    try {
      const data = await apiClient.put(endpoint, { prompt });
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || data.version || 0);
      setSource("table");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await fetchPrompt();
    } catch (err) {
      console.error("Failed to save Luna topic-memory prompt:", err);
      setError(err.data?.error || err.message || "Could not save the prompt.");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateVersion = async (version) => {
    if (!window.confirm(`Restore AI Memory ${label.toLowerCase()} prompt version ${version}?`)) return;
    setLoading(true);
    setSaved(false);
    setError("");
    try {
      await apiClient.put(endpoint, { activateVersion: version });
      await fetchPrompt();
    } catch (err) {
      console.error("Failed to activate Luna topic-memory prompt version:", err);
      setError(err.data?.error || err.message || "Could not restore the prompt version.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        {maintenance
          ? "Luna's nightly maintenance prompt for linked topic memory. Changes take effect for the next maintenance run."
          : "Luna's extraction prompt for curating linked topic memory from completed Irene sessions. Changes take effect for the next extraction."}
        {activeVersion > 0 && <span className="prompt-version-badge">v{activeVersion}</span>}
        {source && <span className="prompt-version-badge">{source}</span>}
      </p>
      {error && <p className="error-message" role="alert">{error}</p>}
      {fetching && <p role="status">Loading prompt...</p>}
      <div className="prompt-token-bar">
        <span>{prompt.length.toLocaleString()} chars</span>
        <span className="prompt-token-sep">·</span>
        <span>~{Math.ceil(prompt.length / 4).toLocaleString()} tokens</span>
      </div>
      <textarea
        className="prompt-editor"
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={`Enter Luna's topic-memory ${label.toLowerCase()} prompt...`}
        spellCheck={false}
        aria-label={`AI Memory ${label.toLowerCase()} prompt`}
        disabled={loading || fetching || !source}
      />
      <div className="prompt-actions">
        <button className="btn-primary" onClick={handleSave} disabled={loading || fetching || !source || !prompt.trim()}>
          <Save size={16} aria-hidden="true" />
          {loading ? "Saving..." : "Save as New Version"}
        </button>
        {saved && <span className="save-confirm">✓ Saved as v{activeVersion}</span>}
        {updatedAt && (
          <span className="prompt-updated">
            Last updated: {new Date(updatedAt).toLocaleString()}
          </span>
        )}
      </div>

      {versions.length > 0 && (
        <div className="prompt-versions">
          <h3>Version History</h3>
          <div className="versions-list">
            {versions.map((version) => (
              <div
                key={version.version}
                className={`version-item ${version.version === activeVersion ? "active" : ""}`}
              >
                <div className="version-info">
                  <span className="version-number">v{version.version}</span>
                  {version.version === activeVersion && <span className="version-active-tag">Active</span>}
                  <span className="version-date">
                    {version.createdAt ? new Date(version.createdAt).toLocaleString() : "—"}
                  </span>
                  <span className="version-chars">
                    {version.charCount?.toLocaleString()} chars · ~{Math.ceil((version.charCount || 0) / 4).toLocaleString()} tokens
                  </span>
                </div>
                <div className="version-preview">{version.preview}</div>
                {version.version !== activeVersion && (
                  <button
                    className="btn-restore"
                    onClick={() => handleActivateVersion(version.version)}
                    disabled={loading || fetching}
                  >
                    Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Sessions list ----

function SessionsList() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveTask, setLiveTask] = useState(null);
  const [liveError, setLiveError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [hydrated, setHydrated] = useState({});
  const [hydrating, setHydrating] = useState({});
  const liveRequestInFlight = useRef(false);
  const wasBusy = useRef(false);

  const fetchSessions = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const data = await apiClient.get("/irene-sessions?limit=50");
      setSessions(data.items || []);
    } catch (err) {
      console.error("Failed to fetch irene sessions:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    let active = true;
    const fetchLive = async () => {
      if (liveRequestInFlight.current) return;
      liveRequestInFlight.current = true;
      try {
        const data = await ireneLiveApi.current();
        if (!active) return;
        const busy = Boolean(data.busy && data.task);
        const raw = data.task || {};
        setLiveTask(busy ? {
          taskId: raw.task_id,
          status: raw.status,
          task: raw.task,
          activity: raw.activity,
          iterationCount: raw.iteration_count,
          commandCount: raw.command_count,
          elapsedSeconds: raw.elapsed_seconds,
          startedAt: raw.started_at,
          updatedAt: raw.updated_at,
          error: raw.error,
        } : null);
        setLiveError("");
        if (wasBusy.current && !busy) fetchSessions(false);
        wasBusy.current = busy;
      } catch (err) {
        if (active) setLiveError(err.message || "Live progress unavailable");
      } finally {
        liveRequestInFlight.current = false;
        if (active) setLiveLoading(false);
      }
    };

    fetchLive();
    const liveTimer = window.setInterval(fetchLive, 2500);
    const sessionsTimer = window.setInterval(() => fetchSessions(false), 15000);
    return () => {
      active = false;
      window.clearInterval(liveTimer);
      window.clearInterval(sessionsTimer);
    };
  }, [fetchSessions]);

  const toggleExpand = async (s) => {
    if (expandedId === s.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(s.id);

    const needsHydration =
      (s.spillover && (s.spillover.bucket || s.spillover.key)) ||
      s.truncated;
    if (needsHydration && !hydrated[s.id] && !hydrating[s.id]) {
      setHydrating((h) => ({ ...h, [s.id]: true }));
      try {
        const data = await apiClient.get(`/irene-sessions?id=${encodeURIComponent(s.id)}`);
        if (data && data.item) {
          setHydrated((h) => ({ ...h, [s.id]: data.item }));
        }
      } catch (err) {
        console.error("Failed to hydrate irene session:", err);
      } finally {
        setHydrating((h) => ({ ...h, [s.id]: false }));
      }
    }
  };

  if (loading || liveLoading) {
    return <div className="placeholder">Loading Irene sessions…</div>;
  }

  if (sessions.length === 0 && !liveTask) {
    return (
      <div className="placeholder">
        No Irene sessions yet. Ask Anna to delegate a terminal task on Semidiosa.
      </div>
    );
  }

  return (
    <div className="output-analysis">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <p className="prompt-analysis-description">
          Irene is Anna's local sysadmin agent on the Semidiosa GPU server.
          Each row is one terminal task — its full Gemma↔Bash transcript, exit
          codes, and elapsed time. Items larger than ~350 KB spill to S3 and
          are hydrated on click.
        </p>
        <RefreshButton onClick={fetchSessions} title="Refresh Irene sessions" />
      </div>

      {liveTask && <LiveTaskCard task={liveTask} />}
      {liveError && <div className="irene-live-error">Live progress unavailable; completed sessions will continue to refresh.</div>}

      <div className="prompt-logs-list">
        {sessions.map((s) => {
          const isExpanded = expandedId === s.id;
          const full = hydrated[s.id] || s;
          const messages = full.messages || [];

          return (
            <div key={s.id} className={`prompt-log-item ${isExpanded ? "expanded" : ""}`}>
              <div className="prompt-log-header" onClick={() => toggleExpand(s)}>
                <div className="prompt-log-meta">
                  <span className="prompt-log-source">{s.runtimeSource === "irene-cloud" ? "irene · cloud" : "irene · local"}</span>
                  {s.model && (
                    <span className="token-badge" title={s.model}>
                      {IRENE_MODEL_OPTIONS.find((option) => option.value === s.model)?.label || s.model}
                      {s.reasoningEffort && s.reasoningEffort !== "none" ? ` / ${s.reasoningEffort}` : ""}
                    </span>
                  )}
                  <span className="prompt-log-time">
                    {s.timestamp ? new Date(s.timestamp).toLocaleString() : "—"}
                  </span>
                  <span className="prompt-log-session" title={s.taskId}>
                    {s.taskId?.slice(0, 12)}
                  </span>
                </div>
                <div className="prompt-log-preview">
                  {(s.task || "(no task)").slice(0, 120)}
                  {(s.task || "").length > 120 ? "…" : ""}
                </div>
                <div className="prompt-log-tokens">
                  {statusBadge(s.status)}
                  <span className="token-badge total">
                    {s.iterationCount || 0} iter
                  </span>
                  {s.commandCount > 0 && (
                    <span className="token-badge chat">
                      {s.commandCount} cmd
                    </span>
                  )}
                  {s.elapsedSeconds > 0 && (
                    <span className="token-badge skills">
                      {s.elapsedSeconds}s
                    </span>
                  )}
                  {s.clarificationRound > 0 && (
                    <span className="token-badge user">
                      clarif #{s.clarificationRound}
                    </span>
                  )}
                  {s.spillover?.bucket && (
                    <span className="token-badge user" title={s.spillover.key}>S3</span>
                  )}
                  {s.truncated && (
                    <span className="token-badge user" style={{ color: "#c33" }}>truncated</span>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="prompt-log-detail">
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem",
                  }}>
                    <span className="section-stat">
                      {hydrating[s.id] ? "Hydrating from S3…" : `task_id ${s.taskId}`}
                    </span>
                    <button
                      type="button"
                      className="section-tab"
                      onClick={() => copyJson(full)}
                    >
                      Copy as JSON
                    </button>
                  </div>

                  <CollapsibleBlock title="Task" defaultOpen={true}>
                    <pre className="prompt-content">{full.task || "(empty)"}</pre>
                    {full.dispatchedTask && full.dispatchedTask !== full.task && (
                      <>
                        <div className="section-header" style={{ marginTop: "0.5rem" }}>
                          <h4>Dispatched (with clarification)</h4>
                        </div>
                        <pre className="prompt-content">{full.dispatchedTask}</pre>
                      </>
                    )}
                  </CollapsibleBlock>

                  {full.report && (
                    <CollapsibleBlock title="Report" defaultOpen={true} stat={`${formatNumber(full.report.length)} chars`}>
                      <pre className="prompt-content">{full.report}</pre>
                    </CollapsibleBlock>
                  )}

                  {full.question && (
                    <CollapsibleBlock title="Question to user" defaultOpen={true}>
                      <pre className="prompt-content">{full.question}</pre>
                    </CollapsibleBlock>
                  )}

                  {full.error && (
                    <CollapsibleBlock title="Error" defaultOpen={true}>
                      <pre className="prompt-content" style={{ color: "#c33" }}>{full.error}</pre>
                    </CollapsibleBlock>
                  )}

                  <CollapsibleBlock
                    title={`Transcript (${messages.length} messages)`}
                    defaultOpen={false}
                  >
                    {messages.length === 0 ? (
                      <p className="empty-section">no transcript</p>
                    ) : (
                      messages.map((m, i) => <IterationCard key={i} message={m} idx={i} />)
                    )}
                  </CollapsibleBlock>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Custom Instructions (CRUD) ----

function ireneAutoResize(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function IreneInstructionForm({ item, onSave, onCancel }) {
  const [content, setContent] = useState(item?.content || "");
  const [category, setCategory] = useState(item?.category || "");
  const [saving, setSaving] = useState(false);
  const textareaRef = useCallback((el) => {
    if (el) {
      ireneAutoResize(el);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onSave({ content: content.trim(), category: category.trim() || undefined });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="version-item active">
      <textarea
        ref={textareaRef}
        className="prompt-editor auto-textarea"
        value={content}
        onChange={(e) => { setContent(e.target.value); ireneAutoResize(e.target); }}
        placeholder='e.g. "When SSHing to devices, always use hostnames from /etc/hosts, never raw IPs."'
        rows={1}
        spellCheck={false}
      />
      <input
        type="text"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        placeholder="category (optional)"
        className="instruction-category-input"
      />
      <div className="instruction-form-actions">
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn-primary" onClick={handleSubmit} disabled={saving || !content.trim()}>
          {saving ? "Saving…" : item ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

function IreneInstructions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/irene-instructions");
      setItems(data.items || []);
    } catch (err) {
      console.error("Failed to fetch irene instructions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCreate = async ({ content, category }) => {
    await apiClient.post("/irene-instructions", { content, category, active: true });
    setCreating(false);
    await fetchItems();
  };

  const handleUpdate = async (item, { content, category }) => {
    await apiClient.put(`/irene-instructions/${item.id}`, { content, category });
    setEditingId(null);
    await fetchItems();
  };

  const handleToggle = async (item) => {
    try {
      await apiClient.put(`/irene-instructions/${item.id}`, { active: !item.active });
      await fetchItems();
    } catch (err) {
      console.error("Failed to toggle instruction:", err);
    }
  };

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/irene-instructions/${item.id}`);
      setDeletingId(null);
      await fetchItems();
    } catch (err) {
      console.error("Failed to delete instruction:", err);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        Custom rules appended to Irene&apos;s prompt at runtime. Use these for
        persistent behaviour tweaks (e.g. &quot;always use hostnames, never raw IPs&quot;,
        &quot;prefer systemctl over direct process management&quot;).
      </p>

      <div className="prompt-section">
        <div className="section-header">
          <h4>Rules ({items.filter((i) => i.active).length} active / {items.length} total)</h4>
          <div className="skills-stats-actions">
            <RefreshButton onClick={fetchItems} title="Refresh instructions" />
            {!creating && (
              <button
                className="icon-btn icon-btn-add"
                onClick={() => { setCreating(true); setEditingId(null); }}
                title="New rule"
                aria-label="New rule"
              >
                +
              </button>
            )}
          </div>
        </div>

        {creating && (
          <IreneInstructionForm onSave={handleCreate} onCancel={() => setCreating(false)} />
        )}

        {loading ? (
          <div className="placeholder">Loading…</div>
        ) : items.length === 0 ? (
          <div className="placeholder">No custom rules yet.</div>
        ) : (
          <div className="versions-list">
            {items.map((it) =>
              editingId === it.id ? (
                <IreneInstructionForm
                  key={it.id}
                  item={it}
                  onSave={(vals) => handleUpdate(it, vals)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div
                  key={it.id}
                  className={`version-item ${it.active ? "active" : ""}`}
                  style={{ opacity: it.active ? 1 : 0.55 }}
                >
                  <div className="version-info">
                    <span className="version-number">{it.active ? "● ON" : "○ off"}</span>
                    {it.category && (
                      <span className="version-active-tag">{it.category}</span>
                    )}
                    <span className="version-date">
                      {it.createdAt ? new Date(it.createdAt).toLocaleDateString() : "—"}
                    </span>
                  </div>
                  <div className="version-preview" style={{ whiteSpace: "pre-wrap" }}>
                    {it.content}
                  </div>
                  {deletingId === it.id ? (
                    <div className="instruction-confirm-bar">
                      <span>Delete this rule?</span>
                      <button className="btn-danger btn-xs" onClick={() => handleDelete(it)}>Yes, delete</button>
                      <button className="btn-secondary btn-xs" onClick={() => setDeletingId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="instruction-actions">
                      <button className="btn-card-action" onClick={() => handleToggle(it)}>
                        {it.active ? "Disable" : "Enable"}
                      </button>
                      <button className="btn-card-action" onClick={() => { setEditingId(it.id); setCreating(false); }}>
                        Edit
                      </button>
                      <button className="btn-card-action btn-card-danger" onClick={() => setDeletingId(it.id)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Main page with tabs ----

function IreneSessions() {
  const [tab, setTab] = useHashTab(IRENE_TAB_IDS, "sessions");
  const tabRefs = useRef([]);

  const handleTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % IRENE_TABS.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + IRENE_TABS.length) % IRENE_TABS.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = IRENE_TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    setTab(IRENE_TABS[nextIndex].id);
    tabRefs.current[nextIndex]?.focus();
  };

  const panels = {
    sessions: <SessionsList />,
    "ai-memory": <IreneTopicMemory />,
    "ai-memory-prompt": <IreneAIMemoryPrompt />,
    core: <IreneCore />,
    "cloud-prompt": <IreneCore cloud />,
    instructions: <IreneInstructions />,
    settings: <><IreneModelSettings /><CallbackReentrySettings /></>,
  };

  return (
    <div className="annacore-page irene-page">
      <div className="annacore-header">
        <div>
          <h2>Irene</h2>
          <p className="annacore-subtitle">Terminal agent on irenewks — sessions, prompts, rules and linked memory</p>
        </div>
      </div>

      <div className="tab-bar" role="tablist" aria-label="Irene sections">
        {IRENE_TABS.map((t, index) => (
          <button
            key={t.id}
            ref={(node) => { tabRefs.current[index] = node; }}
            type="button"
            role="tab"
            id={`irene-tab-${t.id}`}
            aria-controls={`irene-panel-${t.id}`}
            aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            className={`tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`irene-panel-${tab}`}
        aria-labelledby={`irene-tab-${tab}`}
      >
        {panels[tab]}
      </div>
    </div>
  );
}

export default IreneSessions;
